/**
 * Global SAP Reimbursement & Workflow Request Status Enum & Utilities
 * SAP Service Endpoint: ZHR_REIM_REQUEST_STATUS_SRV / Reim_Req_StatusSet
 */

export enum SapRequestStatus {
  NEW = 'N',
  TO_BE_APPROVED = 'T',
  PARTIALLY_APPROVED = 'P',
  APPROVED = 'A',
  REJECTED = 'R',
  PROCESSING_COMPLETE = 'D',
  DELETED = 'X',
  TO_BE_PROCESSED = 'B',
  CLOSED = 'C',
  CANCELLED = 'K',
  SETTLED = 'S',
}

export interface RequestStatusConfig {
  code: string;
  label: string;
  badgeClass: string;
  color: string;
  bgSubtle: string;
  icon: string;
}

export const GLOBAL_REQUEST_STATUS_MAP: Record<string, RequestStatusConfig> = {
  [SapRequestStatus.NEW]: {
    code: 'N',
    label: 'New',
    badgeClass: 'badge bg-info-subtle text-info border border-info-subtle',
    color: '#0dcaf0',
    bgSubtle: '#cff4fc',
    icon: 'ti ti-file-plus',
  },
  [SapRequestStatus.TO_BE_APPROVED]: {
    code: 'T',
    label: 'To Be Approved',
    badgeClass: 'badge bg-warning-subtle text-warning border border-warning-subtle',
    color: '#ffc107',
    bgSubtle: '#fff3cd',
    icon: 'ti ti-clock',
  },
  [SapRequestStatus.PARTIALLY_APPROVED]: {
    code: 'P',
    label: 'Partially Approved',
    badgeClass: 'badge bg-primary-subtle text-primary border border-primary-subtle',
    color: '#0d6efd',
    bgSubtle: '#cfe2ff',
    icon: 'ti ti-progress-check',
  },
  [SapRequestStatus.APPROVED]: {
    code: 'A',
    label: 'Approved',
    badgeClass: 'badge bg-success-subtle text-success border border-success-subtle',
    color: '#198754',
    bgSubtle: '#d1e7dd',
    icon: 'ti ti-circle-check',
  },
  [SapRequestStatus.REJECTED]: {
    code: 'R',
    label: 'Rejected',
    badgeClass: 'badge bg-danger-subtle text-danger border border-danger-subtle',
    color: '#dc3545',
    bgSubtle: '#f8d7da',
    icon: 'ti ti-circle-x',
  },
  [SapRequestStatus.PROCESSING_COMPLETE]: {
    code: 'D',
    label: 'Processing Complete',
    badgeClass: 'badge bg-success-subtle text-success border border-success-subtle',
    color: '#198754',
    bgSubtle: '#d1e7dd',
    icon: 'ti ti-check-all',
  },
  [SapRequestStatus.DELETED]: {
    code: 'X',
    label: 'Deleted',
    badgeClass: 'badge bg-danger-subtle text-danger border border-danger-subtle',
    color: '#dc3545',
    bgSubtle: '#f8d7da',
    icon: 'ti ti-trash',
  },
  [SapRequestStatus.TO_BE_PROCESSED]: {
    code: 'B',
    label: 'To Be Processed',
    badgeClass: 'badge bg-warning-subtle text-warning border border-warning-subtle',
    color: '#ffc107',
    bgSubtle: '#fff3cd',
    icon: 'ti ti-hourglass',
  },
  [SapRequestStatus.CLOSED]: {
    code: 'C',
    label: 'Closed',
    badgeClass: 'badge bg-secondary-subtle text-secondary border border-secondary-subtle',
    color: '#6c757d',
    bgSubtle: '#e2e3e5',
    icon: 'ti ti-lock',
  },
  [SapRequestStatus.CANCELLED]: {
    code: 'K',
    label: 'Cancelled',
    badgeClass: 'badge bg-danger-subtle text-danger border border-danger-subtle',
    color: '#dc3545',
    bgSubtle: '#f8d7da',
    icon: 'ti ti-ban',
  },
  [SapRequestStatus.SETTLED]: {
    code: 'S',
    label: 'Settled',
    badgeClass: 'badge bg-success-subtle text-success border border-success-subtle',
    color: '#198754',
    bgSubtle: '#d1e7dd',
    icon: 'ti ti-wallet',
  },
};

/**
 * Get detailed configuration for a given status code
 */
export const getRequestStatusConfig = (statusCode?: string): RequestStatusConfig => {
  const code = (statusCode || '').trim().toUpperCase();
  return (
    GLOBAL_REQUEST_STATUS_MAP[code] || {
      code,
      label: statusCode || 'Unknown',
      badgeClass: 'badge bg-secondary-subtle text-secondary border border-secondary-subtle',
      color: '#6c757d',
      bgSubtle: '#e2e3e5',
      icon: 'ti ti-help-circle',
    }
  );
};

/**
 * Get display label for a given status code
 */
export const getRequestStatusLabel = (statusCode?: string): string => {
  return getRequestStatusConfig(statusCode).label;
};

/**
 * Get full Bootstrap badge JSX class string for a status code
 */
export const getRequestStatusBadgeClass = (statusCode?: string): string => {
  return getRequestStatusConfig(statusCode).badgeClass;
};

/**
 * Options array for dropdowns / filters
 */
export const GLOBAL_REQUEST_STATUS_OPTIONS = Object.values(GLOBAL_REQUEST_STATUS_MAP).map(
  (config) => ({
    value: config.code,
    label: config.label,
  })
);
